/**
 * 统一错误处理工具
 */

/**
 * 错误类型枚举
 */
export const ErrorType = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

/**
 * 判断是否为认证相关错误
 * @param {Error} error - 错误对象
 * @returns {boolean} 是否为认证错误
 */
export function isAuthError(error) {
  return error.message.includes('认证') ||
         error.message.includes('token') ||
         error.message.includes('登录') ||
         error.message.includes('401');
}

/**
 * 判断是否为网络错误
 * @param {Error} error - 错误对象
 * @returns {boolean} 是否为网络错误
 */
export function isNetworkError(error) {
  return error.name === 'TypeError' && error.message.includes('Failed to fetch') ||
         error.message.includes('Network Error') ||
         error.message.includes('fetch failed');
}

/**
 * 判断是否为服务器错误
 * @param {Error} error - 错误对象
 * @returns {boolean} 是否为服务器错误
 */
export function isServerError(error) {
  return error.message.includes('500') ||
         error.message.includes('服务器') ||
         error.message.includes('内部错误');
}

/**
 * 获取错误类型
 * @param {Error} error - 错误对象
 * @returns {string} 错误类型
 */
export function getErrorType(error) {
  if (isNetworkError(error)) return ErrorType.NETWORK_ERROR;
  if (isAuthError(error)) return ErrorType.AUTH_ERROR;
  if (isServerError(error)) return ErrorType.SERVER_ERROR;
  return ErrorType.UNKNOWN_ERROR;
}

/**
 * 获取用户友好的错误消息
 * @param {Error} error - 错误对象
 * @returns {string} 用户友好的错误消息
 */
export function getUserFriendlyMessage(error) {
  const errorType = getErrorType(error);

  switch (errorType) {
    case ErrorType.NETWORK_ERROR:
      return '网络连接异常，请检查网络后重试';
    case ErrorType.AUTH_ERROR:
      return '登录已过期，请重新登录';
    case ErrorType.SERVER_ERROR:
      return '服务器暂时无法响应，请稍后重试';
    case ErrorType.VALIDATION_ERROR:
      return '输入数据格式错误，请检查后重试';
    default:
      return '操作失败，请稍后重试';
  }
}

/**
 * 创建标准化错误响应
 * @param {string} message - 错误消息
 * @param {string} type - 错误类型
 * @param {any} details - 详细错误信息
 * @returns {object} 标准化错误响应
 */
export function createErrorResponse(message, type = ErrorType.UNKNOWN_ERROR, details = null) {
  return {
    type,
    message,
    details,
    timestamp: new Date().toISOString(),
  };
}

/**
 * API 错误处理装饰器
 * @param {Function} apiFunction - API 函数
 * @returns {Function} 带错误处理的函数
 */
export function withErrorHandling(apiFunction) {
  return async function(...args) {
    try {
      return await apiFunction(...args);
    } catch (error) {
      console.error('API 调用错误:', error);

      // 认证错误特殊处理
      if (isAuthError(error)) {
        // 触发全局登出事件
        window.dispatchEvent(new CustomEvent('auth:logout'));

        // 可以在这里添加用户提示
        showToast('登录已过期，请重新登录');
      }

      // 网络错误重试提示
      if (isNetworkError(error)) {
        showToast('网络连接异常，正在重试...');
      }

      // 重新抛出错误，让调用者处理
      throw error;
    }
  };
}

/**
 * 显示错误提示（这里可以根据项目需求替换为 toast 组件）
 * @param {string} message - 提示消息
 * @param {string} type - 提示类型
 */
export function showToast(message, type = 'error') {
  // 这里可以集成项目中现有的 toast 组件
  if (type === 'error') {
    console.error('Toast Error:', message);
  } else if (type === 'warning') {
    console.warn('Toast Warning:', message);
  } else {
    console.log('Toast Info:', message);
  }

  // 如果项目有 toast 库，可以在这里调用
  // 例如：toast.error(message) 或 toast.warning(message)
}

/**
 * 错误恢复策略
 * @param {Error} error - 错误对象
 * @returns {string} 建议的恢复操作
 */
export function getRecoveryAction(error) {
  const errorType = getErrorType(error);

  switch (errorType) {
    case ErrorType.NETWORK_ERROR:
      return '请检查网络连接后重试';
    case ErrorType.AUTH_ERROR:
      return '请重新登录';
    case ErrorType.SERVER_ERROR:
      return '请稍后重试或联系客服';
    case ErrorType.VALIDATION_ERROR:
      return '检查输入数据后重试';
    default:
      return '稍后重试';
  }
}

/**
 * 统一的错误日志记录
 * @param {Error} error - 错误对象
 * @param {object} context - 错误上下文信息
 */
export function logError(error, context = {}) {
  console.error({
    timestamp: new Date().toISOString(),
    type: getErrorType(error),
    message: error.message,
    stack: error.stack,
    context,
  });

  // 可以在这里添加错误上报逻辑
  // 例如：sentry.captureException(error);
}